import {
  ARBleAzoteFrequency,
  ARBleAzotePeriod,
  ARBleAzoteProductType,
  ARBleCIDestructionPeriod,
  ARBleCIDevelopment,
  ARBleCIType,
  ARBleEtatVegetatifSh,
  ARBlePastResiduType,
  ARBlePrairieAge,
  ARBlePrairieDestructionPeriod,
  ARBlePrairieExploitationMode,
  ARBlePrairiePostDestructionRank,
  ARBlePrairieType,
  ARVerseApportMoAutomne,
  ARVerseApportsMinerauxFractionnement,
} from './agrorendement-ble-lists.model';
import {
  ARConsoleFormatType,
  ARConsoleModulationType,
  ARConsoleType,
  ARConsoleVersion,
  ARFertilizerType,
} from './agrorendement-common-lists.model';
import { Campaign } from './campaign.model';
import { ListItem } from './lists.model';
import { Parcel } from './parcel.model';
import { User } from './user.model';

export class ARConsole {
  consoleType: ARConsoleType;
  consoleVersion: ARConsoleVersion;
  otherConsoleVersion: string;
  consoleFormatType: ARConsoleFormatType;
}

export class ARGCSDownloadInformation {
  downloadUrl: string;
  expiration: number;
}

export class AROrder<C extends Campaign, OI extends AROrderItem<Parcel, any>> {
  id: number;
  campaign: C;
  orderItems: OI[];

  createdAt: string | Date;
  updatedAt: string | Date;
}

export class AROrderItem<P extends Parcel, O extends AROrder<Campaign, any>> {
  id: number;

  parcel: P;
  order: O;
}

export class ARResult<OI extends AROrderItem<any, any>> {
  id: number;

  title: string;
  resultType: ListItem;
  processedOn: string | Date;
  fileReferenceResult: string;

  validator: User;
  validatedAt: string | Date;
  isValidated: boolean;

  madeAvailableAt: string | Date;
  isMadeAvailable: boolean;

  isMarkedForNotification: boolean;
  notifiedAt: string | Date;
  isNotified: boolean;
  notifiedAdvisorsAt: string | Date;
  isNotifiedAdvisors: boolean;

  // C2027-195: expert validation gate and the real restitution date.
  isExpertValidated: boolean;
  expertValidatedAt: string | Date;
  expertValidatedBy: string;
  firstValidatedAt: string | Date;

  orderItems: OI[];

  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface ARVerseData {
  needsVerse: boolean;

  firstAzoteProductType: ARBleAzoteProductType;
  firstAzotePeriod: ARBleAzotePeriod;
  firstAzoteConcentration: number;
  firstAzoteDensity: number;

  secondAzoteProductType: ARBleAzoteProductType;
  secondAzotePeriod: ARBleAzotePeriod;
  secondAzoteConcentration: number;
  secondAzoteDensity: number;

  pastAzoteProductType: ARBleAzoteProductType;
  pastAzoteFrequency: ARBleAzoteFrequency;
}

export interface ARVerseSimpData {
  needsVerse: boolean;
  pastAzoteFrequency: ARBleAzoteFrequency;
  verseApportMoAutomne: ARVerseApportMoAutomne;
  verseApportsMinerauxFractionnement: ARVerseApportsMinerauxFractionnement;
}

export interface ARAgrofertData {
  objective: number;
  needsAgrofert: boolean;
  userProvidedAzote: number;

  etatVegetatifSh: ARBleEtatVegetatifSh;

  hasReliquatSh: boolean;
  reliquatSh: number;

  firstAzoteProductType: ARBleAzoteProductType;
  firstAzotePeriod: ARBleAzotePeriod;
  firstAzoteConcentration: number;
  firstAzoteDensity: number;

  secondAzoteProductType: ARBleAzoteProductType;
  secondAzotePeriod: ARBleAzotePeriod;
  secondAzoteConcentration: number;
  secondAzoteDensity: number;

  pastAzoteProductType: ARBleAzoteProductType;
  pastAzoteFrequency: ARBleAzoteFrequency;

  hasPastAzotePreviousCulture: boolean;
  pastAzotePreviousCultureProductType: ARBleAzoteProductType;
  pastAzotePreviousCultureConcentration: number;
  pastAzotePreviousCultureDensity: number;

  pastResiduType: ARBlePastResiduType;

  hasPrairie: boolean;
  prairieAge: ARBlePrairieAge;
  prairieDestructionPeriod: ARBlePrairieDestructionPeriod;
  prairiePostDestructionRank: ARBlePrairiePostDestructionRank;
  prairieExploitationMode: ARBlePrairieExploitationMode;
  prairieType: ARBlePrairieType;

  hasCi: boolean;
  ciType: ARBleCIType;
  ciDevelopment: ARBleCIDevelopment;
  ciDestructionPeriod: ARBleCIDestructionPeriod;
  isCiEvery2Years: boolean;
}

export interface ARFertilizerData {
  fertilizerType: ARFertilizerType;
  fertilizerConcentration: number;
}

export interface ARVraData {
  consoleModulationType: ARConsoleModulationType;
  consoleType: ARConsoleType;
  consoleVersion: ARConsoleVersion;
  otherConsoleVersion: string;
  consoleFormatType: ARConsoleFormatType;
}

// FIXME une seule table OrderItem (avec tables liées selon le type de culture)
// plus de t2... et T3..., EH

// Façades pour fertilizer data T1 T2 et T3 (blé et orge) + EH (colza)
// SH n'a pas le préfixe SH donc pas besoin de façade
// TODO modèles liés (embedded ? avec JPA) avec les même champs parce que vraiment trop nul
interface AROrderItemWithT1FertilizerData {
  t1FertilizerType: ARFertilizerType;
  t1FertilizerConcentration: number;
}

interface AROrderItemWithT1VraData {
  t1ConsoleModulationType: ARConsoleModulationType;
  t1ConsoleType: ARConsoleType;
  t1ConsoleVersion: ARConsoleVersion;
  t1OtherConsoleVersion: string;
  t1ConsoleFormatType: ARConsoleFormatType;
}

export class ARFertilizerT1DataFacade<OI extends AROrderItemWithT1FertilizerData> {
  constructor(private orderItem: OI) {}

  get fertilizerType(): ARFertilizerType {
    return this.orderItem.t1FertilizerType;
  }
  set fertilizerType(fertilizerType: ARFertilizerType) {
    this.orderItem.t1FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): number {
    return this.orderItem.t1FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: number) {
    this.orderItem.t1FertilizerConcentration = fertilizerConcentration;
  }
}

export class ARVraT1DataFacade<OI extends AROrderItemWithT1VraData> {
  constructor(private orderItem: OI) {}
  get consoleModulationType(): ARConsoleModulationType {
    return this.orderItem.t1ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: ARConsoleModulationType) {
    this.orderItem.t1ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): ARConsoleType {
    return this.orderItem.t1ConsoleType;
  }
  set consoleType(consoleType: ARConsoleType) {
    this.orderItem.t1ConsoleType = consoleType;
  }

  get consoleVersion(): ARConsoleVersion {
    return this.orderItem.t1ConsoleVersion;
  }
  set consoleVersion(consoleVersion: ARConsoleVersion) {
    this.orderItem.t1ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): string {
    return this.orderItem.t1OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: string) {
    this.orderItem.t1OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): ARConsoleFormatType {
    return this.orderItem.t1ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: ARConsoleFormatType) {
    this.orderItem.t1ConsoleFormatType = consoleFormatType;
  }
}

interface AROrderItemWithT2FertilizerData {
  t2FertilizerType: ARFertilizerType;
  t2FertilizerConcentration: number;
}

interface AROrderItemWithT2VraData {
  t2ConsoleModulationType: ARConsoleModulationType;
  t2ConsoleType: ARConsoleType;
  t2ConsoleVersion: ARConsoleVersion;
  t2OtherConsoleVersion: string;
  t2ConsoleFormatType: ARConsoleFormatType;
}

export class ARFertilizerT2DataFacade<OI extends AROrderItemWithT2FertilizerData> {
  constructor(private orderItem: OI) {}

  get fertilizerType(): ARFertilizerType {
    return this.orderItem.t2FertilizerType;
  }
  set fertilizerType(fertilizerType: ARFertilizerType) {
    this.orderItem.t2FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): number {
    return this.orderItem.t2FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: number) {
    this.orderItem.t2FertilizerConcentration = fertilizerConcentration;
  }
}

export class ARVraT2DataFacade<OI extends AROrderItemWithT2VraData> {
  constructor(private orderItem: OI) {}
  get consoleModulationType(): ARConsoleModulationType {
    return this.orderItem.t2ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: ARConsoleModulationType) {
    this.orderItem.t2ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): ARConsoleType {
    return this.orderItem.t2ConsoleType;
  }
  set consoleType(consoleType: ARConsoleType) {
    this.orderItem.t2ConsoleType = consoleType;
  }

  get consoleVersion(): ARConsoleVersion {
    return this.orderItem.t2ConsoleVersion;
  }
  set consoleVersion(consoleVersion: ARConsoleVersion) {
    this.orderItem.t2ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): string {
    return this.orderItem.t2OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: string) {
    this.orderItem.t2OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): ARConsoleFormatType {
    return this.orderItem.t2ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: ARConsoleFormatType) {
    this.orderItem.t2ConsoleFormatType = consoleFormatType;
  }
}

interface AROrderItemWithT3FertilizerData {
  t3FertilizerType: ARFertilizerType;
  t3FertilizerConcentration: number;
}

interface AROrderItemWithT3VraData {
  t3ConsoleModulationType: ARConsoleModulationType;
  t3ConsoleType: ARConsoleType;
  t3ConsoleVersion: ARConsoleVersion;
  t3OtherConsoleVersion: string;
  t3ConsoleFormatType: ARConsoleFormatType;
}

export class ARFertilizerT3DataFacade<OI extends AROrderItemWithT3FertilizerData> {
  constructor(private orderItem: OI) {}

  get fertilizerType(): ARFertilizerType {
    return this.orderItem.t3FertilizerType;
  }
  set fertilizerType(fertilizerType: ARFertilizerType) {
    this.orderItem.t3FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): number {
    return this.orderItem.t3FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: number) {
    this.orderItem.t3FertilizerConcentration = fertilizerConcentration;
  }
}

export class ARVraT3DataFacade<OI extends AROrderItemWithT3VraData> {
  constructor(private orderItem: OI) {}

  get consoleModulationType(): ARConsoleModulationType {
    return this.orderItem.t3ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: ARConsoleModulationType) {
    this.orderItem.t3ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): ARConsoleType {
    return this.orderItem.t3ConsoleType;
  }
  set consoleType(consoleType: ARConsoleType) {
    this.orderItem.t3ConsoleType = consoleType;
  }

  get consoleVersion(): ARConsoleVersion {
    return this.orderItem.t3ConsoleVersion;
  }
  set consoleVersion(consoleVersion: ARConsoleVersion) {
    this.orderItem.t3ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): string {
    return this.orderItem.t3OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: string) {
    this.orderItem.t3OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): ARConsoleFormatType {
    return this.orderItem.t3ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: ARConsoleFormatType) {
    this.orderItem.t3ConsoleFormatType = consoleFormatType;
  }
}

interface AROrderItemWithDnFertilizerData {
  dnFertilizerType: ARFertilizerType;
  dnFertilizerConcentration: number;
}

interface AROrderItemWithDnVraData {
  dnConsoleModulationType: ARConsoleModulationType;
  dnConsoleType: ARConsoleType;
  dnConsoleVersion: ARConsoleVersion;
  dnOtherConsoleVersion: string;
  dnConsoleFormatType: ARConsoleFormatType;
}

export class ARFertilizerDnDataFacade<OI extends AROrderItemWithDnFertilizerData> {
  constructor(private orderItem: OI) {}

  get fertilizerType(): ARFertilizerType {
    return this.orderItem.dnFertilizerType;
  }
  set fertilizerType(fertilizerType: ARFertilizerType) {
    this.orderItem.dnFertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): number {
    return this.orderItem.dnFertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: number) {
    this.orderItem.dnFertilizerConcentration = fertilizerConcentration;
  }
}

export class ARVraDnDataFacade<OI extends AROrderItemWithDnVraData> {
  constructor(private orderItem: OI) {}
  get consoleModulationType(): ARConsoleModulationType {
    return this.orderItem.dnConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: ARConsoleModulationType) {
    this.orderItem.dnConsoleModulationType = consoleModulationType;
  }

  get consoleType(): ARConsoleType {
    return this.orderItem.dnConsoleType;
  }
  set consoleType(consoleType: ARConsoleType) {
    this.orderItem.dnConsoleType = consoleType;
  }

  get consoleVersion(): ARConsoleVersion {
    return this.orderItem.dnConsoleVersion;
  }
  set consoleVersion(consoleVersion: ARConsoleVersion) {
    this.orderItem.dnConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): string {
    return this.orderItem.dnOtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: string) {
    this.orderItem.dnOtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): ARConsoleFormatType {
    return this.orderItem.dnConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: ARConsoleFormatType) {
    this.orderItem.dnConsoleFormatType = consoleFormatType;
  }
}

interface AROrderItemWithEHFertilizerData {
  ehFertilizerType: ARFertilizerType;
  ehFertilizerConcentration: number;
}

interface AROrderItemWithEHVraData {
  ehConsoleModulationType: ARConsoleModulationType;
  ehConsoleType: ARConsoleType;
  ehConsoleVersion: ARConsoleVersion;
  ehOtherConsoleVersion: string;
  ehConsoleFormatType: ARConsoleFormatType;
}

export class ARFertilizerEHDataFacade<OI extends AROrderItemWithEHFertilizerData> {
  constructor(private orderItem: OI) {}

  get fertilizerType(): ARFertilizerType {
    return this.orderItem.ehFertilizerType;
  }
  set fertilizerType(fertilizerType: ARFertilizerType) {
    this.orderItem.ehFertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): number {
    return this.orderItem.ehFertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: number) {
    this.orderItem.ehFertilizerConcentration = fertilizerConcentration;
  }
}

export class ARVraEHDataFacade<OI extends AROrderItemWithEHVraData> {
  constructor(private orderItem: OI) {}
  get consoleModulationType(): ARConsoleModulationType {
    return this.orderItem.ehConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: ARConsoleModulationType) {
    this.orderItem.ehConsoleModulationType = consoleModulationType;
  }

  get consoleType(): ARConsoleType {
    return this.orderItem.ehConsoleType;
  }
  set consoleType(consoleType: ARConsoleType) {
    this.orderItem.ehConsoleType = consoleType;
  }

  get consoleVersion(): ARConsoleVersion {
    return this.orderItem.ehConsoleVersion;
  }
  set consoleVersion(consoleVersion: ARConsoleVersion) {
    this.orderItem.ehConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): string {
    return this.orderItem.ehOtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: string) {
    this.orderItem.ehOtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): ARConsoleFormatType {
    return this.orderItem.ehConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: ARConsoleFormatType) {
    this.orderItem.ehConsoleFormatType = consoleFormatType;
  }
}

export class CbBatchEditT1 {
  constructor(private cbBatchEdit: Object) {}

  get fertilizerType(): boolean {
    return (<any>this.cbBatchEdit).t1FertilizerType;
  }
  set fertilizerType(fertilizerType: boolean) {
    (<any>this.cbBatchEdit).t1FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): boolean {
    return (<any>this.cbBatchEdit).t1FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: boolean) {
    (<any>this.cbBatchEdit).t1FertilizerConcentration = fertilizerConcentration;
  }

  get consoleModulationType(): boolean {
    return (<any>this.cbBatchEdit).t1ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: boolean) {
    (<any>this.cbBatchEdit).t1ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): boolean {
    return (<any>this.cbBatchEdit).t1ConsoleType;
  }
  set consoleType(consoleType: boolean) {
    (<any>this.cbBatchEdit).t1ConsoleType = consoleType;
  }

  get consoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t1ConsoleVersion;
  }
  set consoleVersion(consoleVersion: boolean) {
    (<any>this.cbBatchEdit).t1ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t1OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: boolean) {
    (<any>this.cbBatchEdit).t1OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): boolean {
    return (<any>this.cbBatchEdit).t1ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: boolean) {
    (<any>this.cbBatchEdit).t1ConsoleFormatType = consoleFormatType;
  }
}

export class CbBatchEditT2 {
  constructor(private cbBatchEdit: Object) {}

  get fertilizerType(): boolean {
    return (<any>this.cbBatchEdit).t2FertilizerType;
  }
  set fertilizerType(fertilizerType: boolean) {
    (<any>this.cbBatchEdit).t2FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): boolean {
    return (<any>this.cbBatchEdit).t2FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: boolean) {
    (<any>this.cbBatchEdit).t2FertilizerConcentration = fertilizerConcentration;
  }

  get consoleModulationType(): boolean {
    return (<any>this.cbBatchEdit).t2ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: boolean) {
    (<any>this.cbBatchEdit).t2ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): boolean {
    return (<any>this.cbBatchEdit).t2ConsoleType;
  }
  set consoleType(consoleType: boolean) {
    (<any>this.cbBatchEdit).t2ConsoleType = consoleType;
  }

  get consoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t2ConsoleVersion;
  }
  set consoleVersion(consoleVersion: boolean) {
    (<any>this.cbBatchEdit).t2ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t2OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: boolean) {
    (<any>this.cbBatchEdit).t2OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): boolean {
    return (<any>this.cbBatchEdit).t2ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: boolean) {
    (<any>this.cbBatchEdit).t2ConsoleFormatType = consoleFormatType;
  }
}

export class CbBatchEditT3 {
  constructor(private cbBatchEdit: Object) {}

  get fertilizerType(): boolean {
    return (<any>this.cbBatchEdit).t3FertilizerType;
  }
  set fertilizerType(fertilizerType: boolean) {
    (<any>this.cbBatchEdit).t3FertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): boolean {
    return (<any>this.cbBatchEdit).t3FertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: boolean) {
    (<any>this.cbBatchEdit).t3FertilizerConcentration = fertilizerConcentration;
  }

  get consoleModulationType(): boolean {
    return (<any>this.cbBatchEdit).t3ConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: boolean) {
    (<any>this.cbBatchEdit).t3ConsoleModulationType = consoleModulationType;
  }

  get consoleType(): boolean {
    return (<any>this.cbBatchEdit).t3ConsoleType;
  }
  set consoleType(consoleType: boolean) {
    (<any>this.cbBatchEdit).t3ConsoleType = consoleType;
  }

  get consoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t3ConsoleVersion;
  }
  set consoleVersion(consoleVersion: boolean) {
    (<any>this.cbBatchEdit).t3ConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): boolean {
    return (<any>this.cbBatchEdit).t3OtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: boolean) {
    (<any>this.cbBatchEdit).t3OtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): boolean {
    return (<any>this.cbBatchEdit).t3ConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: boolean) {
    (<any>this.cbBatchEdit).t3ConsoleFormatType = consoleFormatType;
  }
}

export class CbBatchEditDn {
  constructor(private cbBatchEdit: Object) {}

  get fertilizerType(): boolean {
    return (<any>this.cbBatchEdit).dnFertilizerType;
  }
  set fertilizerType(fertilizerType: boolean) {
    (<any>this.cbBatchEdit).dnFertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): boolean {
    return (<any>this.cbBatchEdit).dnFertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: boolean) {
    (<any>this.cbBatchEdit).dnFertilizerConcentration = fertilizerConcentration;
  }

  get consoleModulationType(): boolean {
    return (<any>this.cbBatchEdit).dnConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: boolean) {
    (<any>this.cbBatchEdit).dnConsoleModulationType = consoleModulationType;
  }

  get consoleType(): boolean {
    return (<any>this.cbBatchEdit).dnConsoleType;
  }
  set consoleType(consoleType: boolean) {
    (<any>this.cbBatchEdit).dnConsoleType = consoleType;
  }

  get consoleVersion(): boolean {
    return (<any>this.cbBatchEdit).dnConsoleVersion;
  }
  set consoleVersion(consoleVersion: boolean) {
    (<any>this.cbBatchEdit).dnConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): boolean {
    return (<any>this.cbBatchEdit).dnOtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: boolean) {
    (<any>this.cbBatchEdit).dnOtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): boolean {
    return (<any>this.cbBatchEdit).dnConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: boolean) {
    (<any>this.cbBatchEdit).dnConsoleFormatType = consoleFormatType;
  }
}

export class CbBatchEditEH {
  constructor(private cbBatchEdit: Object) {}

  get fertilizerType(): boolean {
    return (<any>this.cbBatchEdit).ehFertilizerType;
  }
  set fertilizerType(fertilizerType: boolean) {
    (<any>this.cbBatchEdit).ehFertilizerType = fertilizerType;
  }

  get fertilizerConcentration(): boolean {
    return (<any>this.cbBatchEdit).ehFertilizerConcentration;
  }
  set fertilizerConcentration(fertilizerConcentration: boolean) {
    (<any>this.cbBatchEdit).ehFertilizerConcentration = fertilizerConcentration;
  }

  get consoleModulationType(): boolean {
    return (<any>this.cbBatchEdit).ehConsoleModulationType;
  }
  set consoleModulationType(consoleModulationType: boolean) {
    (<any>this.cbBatchEdit).ehConsoleModulationType = consoleModulationType;
  }

  get consoleType(): boolean {
    return (<any>this.cbBatchEdit).ehConsoleType;
  }
  set consoleType(consoleType: boolean) {
    (<any>this.cbBatchEdit).ehConsoleType = consoleType;
  }

  get consoleVersion(): boolean {
    return (<any>this.cbBatchEdit).ehConsoleVersion;
  }
  set consoleVersion(consoleVersion: boolean) {
    (<any>this.cbBatchEdit).ehConsoleVersion = consoleVersion;
  }

  get otherConsoleVersion(): boolean {
    return (<any>this.cbBatchEdit).ehOtherConsoleVersion;
  }
  set otherConsoleVersion(otherConsoleVersion: boolean) {
    (<any>this.cbBatchEdit).ehOtherConsoleVersion = otherConsoleVersion;
  }

  get consoleFormatType(): boolean {
    return (<any>this.cbBatchEdit).ehConsoleFormatType;
  }
  set consoleFormatType(consoleFormatType: boolean) {
    (<any>this.cbBatchEdit).ehConsoleFormatType = consoleFormatType;
  }
}
